import React from 'react';
import { CARD_STYLES, BUTTON_STYLES } from '../../constants/styles';
import { ValidationWarning } from '../Messages/StatusMessages';

const CompactMealCard = ({ meal, actualIndex, onOpenModal, onQuantityChange, isLocked, saving }) => {
    // Validation helper functions
    const warningProteinCount = () => {
        if (!meal.proteinOptions || !Array.isArray(meal.proteinOptions)) return null;
        return meal.proteinOptions.filter(option => option.isActive).length < 1 && meal.proteinOptions.length > 0;
    };

    const warningVeggieCount = () => {
        if (!meal.veggieOptions || !Array.isArray(meal.veggieOptions)) return null;
        return meal.veggieOptions.filter(option => option.isActive).length < 1 && meal.veggieOptions.length > 0;
    };

    const warningStarchCount = () => {
        if (!meal.starchOptions || !Array.isArray(meal.starchOptions)) return null;
        return meal.starchOptions.filter(option => option.isActive).length < 1 && meal.starchOptions.length > 0;
    };

    const warningSauceCount = () => {
        if (!meal.sauceOptions || !Array.isArray(meal.sauceOptions) || meal.meal === 'Add On' || meal.meal === 'Snack' || meal.meal === 'Breakfast') return null;
        return meal.sauceOptions.filter(option => option.isActive).length < 1 && meal.sauceOptions.length > 0;
    };

    // Check if any validation warnings exist
    const hasValidationWarnings = meal.meal !== 'Snack' && meal.quantity > 0 && (
        warningProteinCount() ||
        warningVeggieCount() ||
        warningStarchCount() ||
        warningSauceCount()
    );

    const getValidationMessages = () => {
        const messages = [];
        if (meal.meal !== 'Snack' && meal.meal !== 'Add On' && meal.quantity > 0) {
            if (warningProteinCount()) messages.push('No Protein Selected');
            if (warningVeggieCount()) messages.push('No Veggie Selected');
            if (warningStarchCount()) messages.push('No Starch Selected');
            if (warningSauceCount()) messages.push('No Sauce Selected');
        }
        return messages;
    };

    return (
        <div
            onClick={() => onOpenModal(meal, actualIndex)}
            style={{
                ...CARD_STYLES.container(meal),
                border: hasValidationWarnings ? '2px solid #e74c3c' : CARD_STYLES.container(meal).border,
                boxShadow: hasValidationWarnings
                    ? '0 2px 10px rgba(231, 76, 60, 0.2)'
                    : CARD_STYLES.container(meal).boxShadow
            }}
        >
            {meal.imageUrl && (
                <div
                    style={{
                        ...CARD_STYLES.image,
                        backgroundImage: `url(${meal.imageUrl})`,
                        transition: 'transform 0.2s ease',
                        cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.02)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                    }}
                />
            )}

            {/* Add temporary indicator */}
            {meal.isTemporary && (
                <div style={{
                    fontSize: '14px',
                    color: '#856404',
                    backgroundColor: 'solid #fff3cd',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    marginBottom: '4px',
                    textAlign: 'center'
                }}>
                    Saving...
                </div>
            )}

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Title and quantity controls */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px',
                    flexWrap: 'wrap',
                    gap: '4px'
                }}>
                    <div style={CARD_STYLES.title}>
                        {meal.itemName}
                        {meal.quantity === 0 && (
                            <span style={{
                                marginLeft: '6px',
                                fontSize: '10px',
                                backgroundColor: 'transparent',
                                color: '#1976d2',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                fontWeight: 'normal',
                                border: '1px solid #1976d2'
                            }}>
                                Available
                            </span>
                        )}
                    </div>

                    {/* Edit indicator */}
                    {meal.quantity > 0 && (
                        <div style={{
                            fontSize: '13px',
                            color: '#007bff',
                            fontWeight: 'bold',
                            textDecoration: 'underline',
                            pointerEvents: 'none',
                            flexShrink: 0
                        }}>
                            EDIT
                        </div>
                    )}
                </div>

                {/* Ingredient list at bottom - grouped by category */}
                <div style={{
                    fontSize: '10px',
                    color: '#666',
                    lineHeight: '1.4'
                }}>
                    {(() => {
                        const categories = [
                            { label: 'Protein', options: meal.proteinOptions, showSubs: true },
                            { label: 'Starch', options: meal.starchOptions, showSubs: true },
                            { label: 'Vegetables', options: meal.veggieOptions, showSubs: false },
                            { label: 'Sauce', options: meal.sauceOptions, showSubs: true },
                            { label: 'Garnish', options: meal.garnishOptions, showSubs: false },
                        ];
                        // For available dishes (qty 0), options aren't marked active —
                        // use originalIngredients to identify which options are the defaults
                        const originalIds = new Set((meal.originalIngredients || []).map(i => i.id));
                        const lines = categories
                            .map(({ label, options, showSubs }) => {
                                if (!options || !Array.isArray(options)) return null;
                                const display = meal.quantity > 0
                                    ? options.filter(o => o.isActive && o.name !== 'No Sauce' && o.name !== 'No Starch')
                                    : options.filter(o => originalIds.has(o.id));
                                if (display.length === 0) return null;
                                const stripSF = (s) => (s || '').replace(/^SF /, '');
                                const namesList = display.map(o => {
                                    const cleanSubs = (o.subIngredientNames || []).map(stripSF);
                                    const subs = showSubs && cleanSubs.length > 0
                                        ? ` (${cleanSubs.join(', ')})`
                                        : '';
                                    return { name: stripSF(o.name?.trim()), subs, rawSubs: cleanSubs };
                                });
                                return { label, namesList };
                            })
                            .filter(Boolean);

                        if (lines.length === 0) {
                            // Fallback for dishes without option arrays
                            const ingredients = meal.finalIngredients?.length > 0
                                ? meal.finalIngredients
                                : meal.originalIngredients;
                            return ingredients?.map(i => (i?.name || '').trim()).filter(Boolean).join(', ') || '';
                        }

                        const singleCategory = lines.length === 1;
                        if (singleCategory) {
                            return lines[0].namesList.map((item, i) => {
                                const nameL = item.name?.toLowerCase() || '';
                                const titleL = meal.itemName?.toLowerCase() || '';
                                const sameName = nameL === titleL || titleL.includes(nameL) || nameL.includes(titleL);
                                // Always show subs for single-component dishes
                                const subsText = item.subs || (item.rawSubs.length > 0 ? ` (${item.rawSubs.join(', ')})` : '');
                                if (sameName && !subsText) {
                                    // Name duplicates title with no sub-ingredients — skip
                                    return null;
                                }
                                return <div key={i}><span style={{ fontWeight: '600' }}>{item.name}</span>{subsText}</div>;
                            });
                        }
                        return lines.map((line, i) => (
                            <div key={i}>
                                <span style={{ fontWeight: '600' }}>{line.label}:</span> {line.namesList.map(item => item.name + item.subs).join(', ')}
                            </div>
                        ));
                    })()}
                </div>

                {/* Validation warnings */}
                {hasValidationWarnings && !meal.isTemporary && (
                    <ValidationWarning message={getValidationMessages().join(', ')} />
                )}

                {/* Price (right-aligned) + Qty-left/Buttons-right row — bottom-aligned */}
                <div style={{
                    marginTop: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    paddingTop: '10px'
                }}>
                    {(meal.meal === 'Add On' || meal.meal === 'Snack' || meal.meal === 'Breakfast') && meal.price > 0 && (
                        <span style={{ fontSize: '10px', color: '#666', alignSelf: 'flex-end' }}>
                            Price: ${meal.price}
                        </span>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '5px' }}>
                        <span style={{ fontSize: '10px', color: '#666' }}>
                            Qty: {meal.quantity}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            {meal.quantity > 0 && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onQuantityChange(actualIndex, Math.max(0, meal.quantity - 1));
                                    }}
                                    disabled={isLocked || saving || meal.quantity <= 0}
                                    style={{
                                        ...BUTTON_STYLES.quantity.remove,
                                        cursor: (isLocked || saving || meal.quantity <= 0) ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    Remove
                                </button>
                            )}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onQuantityChange(actualIndex, meal.quantity + 1);
                                }}
                                disabled={isLocked || saving}
                                style={{
                                    ...BUTTON_STYLES.quantity.add,
                                    cursor: (isLocked || saving) ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Add
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompactMealCard;